import { useState, useEffect } from "react";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, RefreshCw, AlertTriangle } from "lucide-react";
import { authFetch } from "../lib/authFetch";
import { API_BASE_URL } from '../lib/authFetch';

interface Plano {
  id: number;
  nome: string;
  slug: string;
  valor: number;
  descricao: string;
  max_cameras: number;
  max_usuarios: number;
  max_consultas_mes: number;
  recursos: string[];
  destaque: boolean;
}

interface PlansModalProps {
  open: boolean;
  subscription: any;
  onClose: () => void;
  onPlanChanged: (newSubscription: any) => void;
}

export default function PlansModal({ open, subscription, onClose, onPlanChanged }: PlansModalProps) {
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadPlanos();
    }
  }, [open]);

  const loadPlanos = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/planos`);
      const data = await response.json();
      setPlanos(data.planos || []);
    } catch {
      // Erro ao carregar planos
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-2 sm:p-4">
      <div className="bg-protec-dark border border-[0.01px] border-protec-gold p-4 sm:p-8 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto my-2 sm:my-4">
        <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-8 text-protec-gold text-center">Escolha seu plano</h2>
        
        {loading ? (
          <div className="text-center py-10 sm:py-16">
            <RefreshCw className="w-8 h-8 sm:w-10 sm:h-10 text-protec-gold animate-spin mx-auto mb-4" />
            <p className="text-gray-400 text-sm sm:text-base">Carregando planos...</p>
          </div>
        ) : planos.length === 0 ? (
          <div className="text-center py-10 sm:py-16">
            <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-500 mx-auto mb-4" />
            <p className="text-gray-400 text-sm sm:text-base">Nenhum plano disponível no momento</p>
            <button
              className="mt-4 px-4 sm:px-6 py-2 border border-protec-gold text-protec-gold rounded hover:bg-protec-gold hover:text-black text-sm sm:text-base"
              onClick={onClose}
            >
              Fechar
            </button>
          </div>
        ) : (
          <div className={`grid gap-3 sm:gap-4 ${
            planos.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : 
            planos.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 
            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          }`}>
            {planos.map((plano) => (
              <Card
                key={plano.id}
                className={`bg-protec-dark-light border-protec-gold/20 relative flex flex-col h-full ${plano.destaque ? 'ring-2 ring-protec-gold' : ''}`}
              >
                {plano.destaque && (
                  <Badge className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-protec-gold text-black text-xs">
                    Mais Popular
                  </Badge>
                )}
                <div className="flex-1 flex flex-col">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-white text-base sm:text-lg">{plano.nome}</CardTitle>
                    <CardDescription className="text-gray-300 text-xs sm:text-sm">
                      {plano.descricao}
                    </CardDescription>
                    <div className="flex items-baseline">
                      <span className="text-2xl sm:text-3xl font-bold gradient-text">{formatCurrency(plano.valor)}</span>
                      <span className="text-gray-400 text-sm">/mês</span>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col">
                    {plano.recursos && plano.recursos.length > 0 && (
                      <ul className="space-y-1 sm:space-y-2 mb-4 sm:mb-6">
                        {plano.recursos.map((recurso, idx) => (
                          <li key={idx} className="flex items-center text-xs sm:text-sm text-gray-300">
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-protec-gold mr-2 flex-shrink-0" />
                            {recurso}
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </div>
                <button
                  className={`w-4/5 mx-auto mb-4 sm:mb-5 ${plano.destaque
                    ? 'bg-gradient-gold text-black hover:shadow-gold'
                    : 'bg-protec-dark border border-protec-gold text-protec-gold hover:bg-protec-gold hover:text-black'
                  } font-semibold py-2 rounded text-sm sm:text-base`}
                  onClick={async () => {
                    const token = localStorage.getItem("token");
                    if (token && subscription) {
                      // Atualizar assinatura existente
                      const response = await authFetch(`${API_BASE_URL}/subscriptions/${subscription.id}`, {
                        method: "PUT",
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          plan: plano.nome,
                          monthly_price: plano.valor,
                          queries_limit: plano.max_consultas_mes === -1 ? 999999 : plano.max_consultas_mes
                        })
                      });
                      if (response.ok) {
                        window.dispatchEvent(new Event('atividade-registrada'));
                        const updated = await response.json();
                        onPlanChanged(updated);
                        onClose();
                      }
                    } else {
                      // Registro inicial
                      if (onPlanChanged) onPlanChanged(plano.nome);
                      onClose();
                    }
                  }}
                >
                  Escolher {plano.nome}
                </button>
              </Card>
            ))}
          </div>
        )}

        <div className="text-center mt-4 sm:mt-6">
          <button
            className="text-gray-400 hover:text-white underline text-sm sm:text-base"
            onClick={onClose}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
